// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushJitter.js
export function hash32(value) {
  let n = value | 0;
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = n ^ (n >>> 16);
  return n >>> 0;
}

export function combineSeed(...parts) {
  let seed = 0x811c9dc5;
  for (let i = 0; i < parts.length; i += 1) {
    const value = Number.isFinite(parts[i]) ? Math.round(parts[i]) : 0;
    seed ^= hash32(value + i * 374761393);
    seed = Math.imul(seed, 16777619);
  }
  return seed >>> 0;
}

export function seededUnit(seed, index, channel = 0) {
  const value = hash32(
    (seed | 0)
    ^ Math.imul((index + 1) | 0, 0x9e3779b1)
    ^ Math.imul((channel + 1) | 0, 0x85ebca6b)
  );
  return value / 4294967295;
}

export function seededSignedUnit(seed, index, channel = 0) {
  return seededUnit(seed, index, channel) * 2 - 1;
}

export function createSeededRng(seed, index = 0, channel = 0) {
  let state = combineSeed(seed, index, channel) || 0x6d2b79f5;

  return function nextSeededRandom() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededRange(seed, index, channel, min, max) {
  return min + (max - min) * seededUnit(seed, index, channel);
}

export function seededSignedRange(seed, index, channel, amount) {
  return seededSignedUnit(seed, index, channel) * amount;
}

export function clampJitterAmount(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

let strokeSeedCounter = 0;

export function createStrokeSeed(point, options) {
  strokeSeedCounter = (strokeSeedCounter + 1) >>> 0;

  return combineSeed(
    Date.now(),
    strokeSeedCounter,
    point && Number.isFinite(point.x) ? point.x * 100 : 0,
    point && Number.isFinite(point.y) ? point.y * 100 : 0,
    options && Number.isFinite(options.size) ? options.size : 0,
    options && Number.isFinite(options.softness) ? options.softness : 0,
    options && Number.isFinite(options.opacity) ? options.opacity * 1000 : 0,
    options && Number.isFinite(options.jitterAmount) ? options.jitterAmount : 0
  );
}

export function createJitterBrushSettings(size, jitterAmount) {
  const safeAmount = clampJitterAmount(jitterAmount);
  const jitterRatio = safeAmount / 100;
  const radius = Math.max(0.5, Number(size || 0) / 2);

  const minSpacingPx = Math.max(0.5, radius * 0.12);
  const maxSpacingPx = Math.max(minSpacingPx + 0.01, radius * 1.32);
  const spacingEase = Math.pow(jitterRatio, 0.82);
  const spacingPx = maxSpacingPx - (maxSpacingPx - minSpacingPx) * spacingEase;

  const scatterEase = Math.pow(jitterRatio, 1.35);
  const rotationEase = Math.pow(jitterRatio, 1.15);
  const sizeEase = Math.pow(jitterRatio, 1.2);
  const elongationEase = Math.pow(jitterRatio, 1.05);

  const blobScale = 1 - jitterRatio * 0.18;

  return {
    jitterAmount: safeAmount,
    jitterRatio,
    spacingPx,
    rotationJitter: 4 + rotationEase * 42,
    sizeJitter: 0.008 + sizeEase * 0.08,
    scatter: 0.004 + scatterEase * 0.48,
    elongation: 0.02 + elongationEase * 0.18,
    blobScale,
  };
}

export function getJitterBrushSpacing(size, settings) {
  const radius = Math.max(0.5, Number(size || 0) / 2);
  const explicitSpacing = Number(settings && settings.spacingPx);
  if (Number.isFinite(explicitSpacing) && explicitSpacing > 0) {
    return Math.max(0.5, explicitSpacing);
  }
  return Math.max(0.5, radius * 0.6);
}

export function getJitterBlobScale(settings) {
  const scale = Number(settings && settings.blobScale);
  if (!Number.isFinite(scale)) return 1;
  return Math.max(0.72, Math.min(1, scale));
}

export function buildJitterBlobProfile(seed, dabIndex, radius, settings) {
  const rotationJitter = Math.max(0, Number(settings && settings.rotationJitter) || 0);
  const sizeJitter = Math.max(0, Number(settings && settings.sizeJitter) || 0);
  const scatter = Math.max(0, Number(settings && settings.scatter) || 0);
  const elongation = Math.max(0, Number(settings && settings.elongation) || 0);

  const baseStretchMin = 0.94 - Math.min(0.16, elongation * 0.16);
  const baseStretchMax = 1.01 + Math.min(0.2, elongation * 0.2);
  const rotation = seededSignedUnit(seed, dabIndex, 1) * (rotationJitter * Math.PI / 180);
  const sizeScale = 1 + seededSignedUnit(seed, dabIndex, 2) * sizeJitter;

  const stretchX = (
    baseStretchMin + seededUnit(seed, dabIndex, 3) * (baseStretchMax - baseStretchMin)
  ) * Math.max(0.82, sizeScale);

  const stretchY = (
    baseStretchMin + seededUnit(seed, dabIndex, 4) * (baseStretchMax - baseStretchMin)
  ) * Math.max(0.82, 1 - sizeJitter * 0.35 + seededUnit(seed, dabIndex, 5) * sizeJitter * 0.7);

  const offsetScale = radius * scatter;

  return {
    rotation,
    stretchX,
    stretchY,
    offsetX: seededSignedUnit(seed, dabIndex, 6) * offsetScale,
    offsetY: seededSignedUnit(seed, dabIndex, 7) * offsetScale,
  };
}